import React, { useState, useEffect } from "react"
import {
  Container,
  Button,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  UncontrolledAlert,  // Import UncontrolledAlert for alert message
} from "reactstrap"
import classnames from "classnames"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { Link, useParams } from "react-router-dom"
import { scanConfigs } from "./scanConfigs"

import scanCards from "../../../common/data/scancards";
import axios from "axios"
import CredentialsTab from "../credentials/CredentialsTab"

const ScanOptions = () => {
  document.title = "Scan Options"

  const { scanType } = useParams() // Only use scanType from URL params
  const obj = JSON.parse(localStorage.getItem("authUser"));
  const userId = obj.username;


  const [customActiveTab, setCustomActiveTab] = useState("1")
  const [formData, setFormData] = useState({})
  const [selectedScanType, setSelectedScanType] = useState(scanType) // Track user's selected scanType
  const [alertVisible, setAlertVisible] = useState(false)  // State to control alert visibility
  const [alertMessage, setAlertMessage] = useState("")     // Alert message state
  const [alertColor, setAlertColor] = useState("success")  // Set default alert color as success
  const selectedScan = scanCards.find(card => card.scanType === selectedScanType);
  const [files, setFiles] = useState({});

  const handleFileChange = (e) => {
    const { id, files: uploadedFiles } = e.target;
    if (uploadedFiles && uploadedFiles[0]) {
      setFiles(prev => ({
        ...prev,
        [id]: uploadedFiles[0]
      }));
    }
  };

  const toggleCustom = tab => {
    if (customActiveTab !== tab) {
      setCustomActiveTab(tab)
    }
  }
  
  const handleInputChange = e => {
    const { id, value } = e.target
    setFormData(prevFormData => ({
      ...prevFormData,
      [id]: value,
    }))
  }

  const renderFields = fields => {
    return fields.map(field => {
      const isFieldEmpty = !formData[field.id];
      const isFieldInvalid = field.required && isFieldEmpty;
  
      const fieldStyle = {
        width: field.width || "100%", // Default to 100% width if not specified
        height: field.height || "auto", // Default to auto height if not specified
        resize: field.type === "textarea" ? "none" : undefined, // Disable resize for textarea
      }
  
      // For file inputs
      if (field.type === 'file') {
        return (
          <FormGroup key={field.id}>
            <Label htmlFor={field.id}>{field.label}</Label>
            <Input
              type="file"
              id={field.id}
              onChange={handleFileChange}
              className={`form-control ${isFieldInvalid ? 'is-invalid' : ''}`}
              accept={field.accept}
              required={field.required || false}
            />
            {isFieldInvalid && <div className="invalid-feedback">This field is required</div>}
          </FormGroup>
        );
      }
  
      // For select inputs
      if (field.type === "select") {
        return (
          <FormGroup key={field.id}>
            <Label htmlFor={field.id}>{field.label}</Label>
            <Input
              type="select"
              id={field.id}
              value={formData[field.id] || ""}
              onChange={handleInputChange}
              className={`custom-select ${isFieldInvalid ? 'is-invalid' : ''}`}
              style={fieldStyle}
              required={field.required || false}
            >
              {field.options.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Input>
            {isFieldInvalid && <div className="invalid-feedback">This field is required</div>}
          </FormGroup>
        );
      }
  
      // For regular text inputs
      return (
        <FormGroup key={field.id}>
          <Label htmlFor={field.id}>{field.label}</Label>
          <Input
            type={field.type}
            id={field.id}
            value={formData[field.id] || ""}
            onChange={handleInputChange}
            placeholder={field.placeholder || ""}
            style={fieldStyle}
            className={`form-control ${isFieldInvalid ? 'is-invalid' : ''}`}
            required={field.required || false}
          />
          {isFieldInvalid && <div className="invalid-feedback">This field is required</div>}
        </FormGroup>
      );
    })
  }
  

  useEffect(() => {
    const initializeFormData = (fields, initialValues = {}) => {
      fields.forEach(field => {
        if (field.type === "group") {
          initializeFormData(field.fields, initialValues)
        } else {
          initialValues[field.id] = "" // Default to empty string for all fields
        }
      })
      return initialValues
    }

    const config = scanConfigs[scanType] || scanConfigs["webApp"]
    const initialValues = config.tabs.reduce(
      (acc, tab) => initializeFormData(tab.fields, acc),
      {}
    )

    // Set the form data with initial values
    setFormData(initialValues)
  }, [scanType])

  const config = scanConfigs[scanType] || scanConfigs["webApp"]
  
  const handleDataChange = (data) => {
    formData.credentials = data    
    // Do something with the data (e.g., save to state, send to API)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  // Check for missing required fields
  const missingFields = Object.keys(formData).filter((key) => {
    const field = config.tabs
      .flatMap((tab) => tab.fields)
      .find((field) => field.id === key);
    return field && field.required && !formData[key];
  });

  if (missingFields.length > 0) {
    // Show alert for missing required fields
    setAlertMessage(`Please fill in the following required fields: ${missingFields.join(', ')}`);
    setAlertColor("warning");
    setAlertVisible(true);
    return; // Prevent form submission
  }
    if (!userId) {
      return;
    }
  
    // Create a new FormData instance to handle file uploads
    const formDataInstance = new FormData();
  
    // Add all the regular form data from state to FormData instance
    config.tabs.forEach(tab => {
      const tabData = {};
      tab.fields.forEach(field => {
        if (field.type === 'file') {
          // Add file to FormData if it exists in the files state
          if (files[field.id]) {
            formDataInstance.append(field.id, files[field.id]);
          }
        } else {
          // Add regular form data from state.formData to the FormData instance
          const fieldValue = formData[field.id] || ""; // Get field value from state.formData
          tabData[field.id] = fieldValue;
        }
      });
  
      // Append tab data as JSON
      formDataInstance.append(tab.title, JSON.stringify(tabData));
    });
  
    // Add schedule data to FormData instance
    const scheduleData = {
      frequency: formData.scheduleFrequency || "",
      time: formData.scheduleTime || "",
      timezone: formData.scheduleTimezone || ""
    };
  
    formDataInstance.append('schedule', JSON.stringify(scheduleData));
  
    // Add credentials data to FormData instance (if any)
    formDataInstance.append('credentials', JSON.stringify(formData.credentials || {}));
  
    // Add the scan type to FormData
    formDataInstance.append('mainscanType', selectedScanType);
  
  
  
    // Make the API call to submit the form
    try {
      const response = await axios.post(
        `https://api.compliance.cx/api/vulnerability-scan/${userId}`,
        formDataInstance,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setAlertMessage("Analysis in Progress!");
      setAlertColor("success");
      setAlertVisible(true);
  
      setTimeout(() => {
        setAlertVisible(false);
        setFormData({});
        setFiles({});
        setSelectedScanType(scanType);
      }, 3000);
  
    } catch (error) {
      setAlertMessage("Error 121. Please Try Later");
      setAlertColor("danger");
      setAlertVisible(true);
      setTimeout(() => setAlertVisible(false), 3000);
    }
  };
  

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Scan" breadcrumbItem="Scan Options" />
        <Row>
          <Col>
            <Link to="/vulnerabilityScan" className="btn btn-link">
              &larr; Back to Scans
            </Link>
          </Col>
        </Row>


      {/* Row to display the selected scan card */}
      <Row>
  <Col xl={12}>
    <Card className="shadow-sm mb-4">
      <CardBody>
      
        <h4 className="mt-3 text-start">{selectedScan.title}</h4>
        <p className="text-start">{selectedScan.description}</p>
      </CardBody>
    </Card>
  </Col>
</Row>



        {/* Alert Message */}
        {alertVisible && (
          <UncontrolledAlert color={alertColor} className="alert-dismissible fade show" role="alert">
            {alertMessage}
          </UncontrolledAlert>
        )}

        <div className="checkout-tabs">
          <Row>
            <Col xl={12}>
              <Card>
                <CardBody>
                  <Nav tabs className="nav-tabs-custom">
                    {config.tabs.map(tab => (
                      <NavItem key={tab.id}>
                        <NavLink
                          className={classnames({
                            active: customActiveTab === tab.id,
                          })}
                          onClick={() => toggleCustom(tab.id)}
                        >
                          {tab.title}
                        </NavLink>
                      </NavItem>
                    ))}

                    <NavItem>
                      <NavLink
                        className={classnames({
                          active: customActiveTab === "credentials",
                        })}
                        onClick={() => toggleCustom("credentials")}
                      >
                        Credentials
                      </NavLink>
                    </NavItem>

                    {/* Add the Schedule tab */}
                    <NavItem>
                      <NavLink
                        className={classnames({
                          active: customActiveTab === "schedule",
                        })}
                        onClick={() => toggleCustom("schedule")}
                      >
                        Schedule
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={customActiveTab} className="mt-4">
                    {config.tabs.map(tab => (
                      <TabPane tabId={tab.id} key={tab.id}>
                        <Row className="mt-3">{renderFields(tab.fields)}</Row>
                      </TabPane>
                    ))}
                    <TabPane tabId="credentials">
                      <div   className="d-flex justify-content-center align-items-center">
                      {/* <CredentialsTab onCredentialsUpdate={handleDataChange} /> */}
                      <Card className="text-center shadow-sm" style={{ maxWidth: '500px', width: '100%' }}>
                      <CardBody>
                        <p>Connection to BluHawk Secure Layer Protocol is required for authenticated access.</p>
                      </CardBody>
                    </Card>
                    </div>
                    </TabPane>

                    {/* Add the Schedule content */}
                    <TabPane tabId="schedule">
                      <Row className="mt-3">
                        {/* Frequency Field */}
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="scheduleFrequency">Frequency</Label>
                            <Input
                              type="select"
                              id="scheduleFrequency"
                              value={formData.scheduleFrequency || ""} // Default to empty string
                              onChange={handleInputChange}
                              className="custom-select"
                            >
                              <option value="">Select Frequency</option>{" "}
                              <option value="scanonchange">Scan on Change</option>
                              <option value="once">Once</option>
                              <option value="daily">Daily</option>
                              <option value="weekly">Weekly</option>
                              <option value="monthly">Monthly</option>
                            </Input>
                          </FormGroup>
                        </Col>

                        {/* Time Field */}
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="scheduleTime">Time</Label>
                            <Input
                              type="time"
                              id="scheduleTime"
                              value={formData.scheduleTime || ""}
                              onChange={handleInputChange}
                              className="form-control"
                              disabled={formData.scheduleFrequency === "scanonchange"} // Disable when Scan on Change is selected
                            />
                          </FormGroup>
                        </Col>

                        {/* Timezone Field */}
                        <Col md={4}>
                          <FormGroup>
                            <Label htmlFor="scheduleTimezone">Timezone</Label>
                            <Input
                              type="select"
                              id="scheduleTimezone"
                              value={formData.scheduleTimezone || ""}
                              onChange={handleInputChange}
                              className="custom-select"
                              disabled={formData.scheduleFrequency === "scanonchange"} // Disable when Scan on Change is selected
                            >
                              <option value="GMT">GMT</option>
                              <option value="PST">PST</option>
                              <option value="EST">EST</option>
                              <option value="CST">CST</option>
                              <option value="MST">MST</option>
                            </Input>
                          </FormGroup>
                        </Col>
                      </Row>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>

        <Row>
          <Col>
            <button onClick={handleSubmit} className="btn btn-primary">
              Submit Scan
            </button>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

export default ScanOptions

